import {
    collection,
    doc,
    getDocs,
    getDoc,
    addDoc,
    updateDoc,
    query,
    where,
    Timestamp
} from 'firebase/firestore';
import { db } from '../../config/firebase';

// Primary collection for employee data
const EMPLOYEES_COLLECTION = 'employees';

/**
 * Helper to convert a browser File object to a Base64 string with optional compression
 */
const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        if (!file || !(file instanceof File)) {
            resolve(null);
            return;
        }

        // Check non-image file size limit
        if (!file.type.startsWith('image/') && file.size > 800 * 1024) {
            reject(new Error(`File "${file.name}" is too large (${(file.size / 1024).toFixed(1)} KB). Documents must be smaller than 800 KB to be saved.`));
            return;
        }

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const result = reader.result;
            
            // Compress any image that is larger than 150KB
            if (file.type.startsWith('image/') && file.size > 150 * 1024) {
                console.log(`[EmployeeService] Image "${file.name}" is ${(file.size/1024).toFixed(1)} KB. Compressing...`);
                const img = new Image();
                img.src = result;
                img.onload = () => {
                    try {
                        const canvas = document.createElement('canvas');
                        let width = img.width;
                        let height = img.height;
                        
                        // Scale down to a reasonable max width for small screen UI/cards
                        const MAX_WIDTH = 800;
                        if (width > MAX_WIDTH) {
                            height = (MAX_WIDTH / width) * height;
                            width = MAX_WIDTH;
                        }
                        
                        canvas.width = width;
                        canvas.height = height;
                        const ctx = canvas.getContext('2d');
                        
                        // Fill canvas with white background to support transparent PNGs when converting to JPEG
                        ctx.fillStyle = '#FFFFFF';
                        ctx.fillRect(0, 0, width, height);
                        
                        ctx.drawImage(img, 0, 0, width, height);
                        
                        // Output as image/jpeg with 0.6 quality for excellent compression
                        let compressedDataUrl = canvas.toDataURL('image/jpeg', 0.6);
                        
                        // If it's somehow still too large, compress further
                        if (compressedDataUrl.length > 800 * 1024) {
                            console.log('[EmployeeService] Compressed image still large, applying aggressive compression...');
                            canvas.width = width * 0.7;
                            canvas.height = height * 0.7;
                            ctx.fillStyle = '#FFFFFF';
                            ctx.fillRect(0, 0, canvas.width, canvas.height);
                            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                            compressedDataUrl = canvas.toDataURL('image/jpeg', 0.4);
                        }
                        
                        console.log(`[EmployeeService] Compressed "${file.name}" to: ${(compressedDataUrl.length/1024).toFixed(1)} KB`);
                        
                        if (compressedDataUrl.length > 950 * 1024) {
                            reject(new Error(`Image "${file.name}" is too large and cannot be compressed below the Firestore limit. Please upload a smaller image.`));
                            return;
                        }
                        
                        resolve({
                            name: file.name,
                            type: 'image/jpeg',
                            size: compressedDataUrl.length,
                            data: compressedDataUrl,
                            uploadedAt: new Date().toISOString()
                        });
                    } catch (err) {
                        reject(err);
                    }
                };
                img.onerror = (err) => {
                    reject(new Error('Failed to load image for compression: ' + err.message));
                };
                return;
            }

            // Ensure even non-compressed files don't exceed the Firestore limit
            if (result.length > 950 * 1024) {
                reject(new Error(`File "${file.name}" exceeds the maximum size limit allowed by the database.`));
                return;
            }

            resolve({
                name: file.name,
                type: file.type,
                size: file.size,
                data: result,
                uploadedAt: new Date().toISOString()
            });
        };
        reader.onerror = (error) => reject(error);
    });
};

/**
 * Remove all File objects and circular references for Firestore
 */
const cleanupForFirestore = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    
    // Handle Arrays
    if (Array.isArray(obj)) {
        return obj.map(item => cleanupForFirestore(item));
    }
    
    // Create a plain object copy
    const cleaned = {};
    for (const key in obj) {
        const value = obj[key];
        
        // Skip functions and undefined
        if (typeof value === 'function' || value === undefined) continue;
        
        // Skip File/Blob (using various checks)
        if (value instanceof File || (value && value.constructor && value.constructor.name === 'File') || 
            value instanceof Blob || (value && value.constructor && value.constructor.name === 'Blob')) {
            continue;
        }
        
        // Recursively handle objects
        if (typeof value === 'object' && value !== null && !(value instanceof Timestamp)) {
            cleaned[key] = cleanupForFirestore(value);
        } else {
            cleaned[key] = value;
        }
    }
    return cleaned;
};

/**
 * Normalize employee data from different structures
 * Handles both 'employees' collection (personalInfo/jobDetails) and 'users' collection (personal/job)
 */
const normalizeEmployeeData = (data, docId) => {
    // Check which structure we have
    const hasPersonalInfo = !!data.personalInfo;
    const hasPersonal = !!data.personal;
    
    let personal, job, bank, salary;
    
    if (hasPersonalInfo) {
        // Backend 'employees' collection structure
        personal = {
            firstName: data.personalInfo?.firstName || '',
            lastName: data.personalInfo?.lastName || '',
            fullName: `${data.personalInfo?.firstName || ''} ${data.personalInfo?.lastName || ''}`.trim(),
            email: data.personalInfo?.email || '',
            phone: data.personalInfo?.phone || '',
            dateOfBirth: data.personalInfo?.dateOfBirth || '',
            gender: data.personalInfo?.gender || '',
            currentAddress: data.personalInfo?.address || {},
            photo: data.personalInfo?.photo || data.profilePicture || ''
        };
        job = {
            employeeCode: data.employeeCode || docId,
            department: data.jobDetails?.department || '',
            designation: data.jobDetails?.position || data.jobDetails?.designation || '',
            employmentType: data.jobDetails?.employmentType || '',
            joiningDate: data.jobDetails?.joinDate || data.jobDetails?.joiningDate || '',
            reportingManager: data.jobDetails?.reportingTo || ''
        };
        bank = data.bankDetails || {};
        salary = data.salaryStructure || {};
    } else if (hasPersonal) {
        // 'users' collection structure
        personal = data.personal;
        job = data.job || {};
        bank = data.bank || {};
        salary = data.salary || {};
    } else {
        // Flat structure (root level fields)
        personal = {
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            fullName: data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim(),
            email: data.email || '',
            phone: data.phone || data.phoneNumber || '',
            dateOfBirth: data.dateOfBirth || data.dob || '',
            gender: data.gender || '',
            photo: data.photo || data.profilePicture || ''
        };
        job = {
            employeeCode: data.employeeCode || data.employeeId || docId,
            department: data.department || '',
            designation: data.designation || data.position || '',
            employmentType: data.employmentType || '',
            joiningDate: data.joiningDate || data.joinDate || ''
        };
        bank = {};
        salary = {};
    }
    
    const isActuallyInactive = (data.status || '').toLowerCase() === 'inactive' || data.isActive === false;

    // Resolve joining date from various nested or root structures
    const resolvedJoiningDate = job.joiningDate 
        || data.jobDetails?.joinDate 
        || data.jobDetails?.joiningDate 
        || data.job?.joiningDate 
        || data.job?.joinDate 
        || data.joiningDate 
        || data.joinDate 
        || personal.joiningDate 
        || '';

    // Resolve inactive date from exitDate, inactiveDate, deactivatedAt, relievingDate, resignationDate, or updatedAt when inactive
    const resolvedInactiveDate = data.inactiveDate 
        || data.exitDate 
        || data.deactivatedAt 
        || data.relievingDate 
        || data.resignationDate 
        || data.leftDate 
        || data.jobDetails?.exitDate 
        || data.jobDetails?.inactiveDate 
        || data.job?.exitDate 
        || data.job?.inactiveDate 
        || (isActuallyInactive ? (data.updatedAt || data.modifiedAt || null) : null);

    return {
        id: docId,
        ...data,
        personal,
        job,
        bank,
        salary,
        // Root level fields for list display
        firstName: personal.firstName,
        lastName: personal.lastName,
        email: personal.email,
        employeeId: job.employeeCode,
        department: job.department,
        designation: job.designation,
        employmentType: job.employmentType || data.employmentType || '',
        // Determine active status - check both status and isActive fields
        status: isActuallyInactive ? 'Inactive' : 'Active',
        isActive: !isActuallyInactive,
        joiningDate: resolvedJoiningDate,
        inactiveDate: resolvedInactiveDate,
        exitDate: resolvedInactiveDate
    };
};

/**
 * Get all employees with robust client-side filtering
 * Fetches from 'employees' collection (primary source)
 */
export const getEmployees = async (filters = {}) => {
    try {
        let employees = [];
        
        // Fetch from 'employees' collection (primary source with full employee data)
        const employeesRef = collection(db, EMPLOYEES_COLLECTION);
        const employeesSnapshot = await getDocs(query(employeesRef));
        
        employeesSnapshot.forEach((docSnap) => {
            const data = docSnap.data();
            
            // Skip deleted employees
            if (data.isDeleted === true) return;
            
            const normalized = normalizeEmployeeData(data, docSnap.id);
            
            // Convert timestamps
            normalized.createdAt = data.createdAt?.toDate?.()?.toISOString() || data.createdAt;
            normalized.updatedAt = data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt;
            
            // Handle status properly
            const isInactive = (data.status || '').toLowerCase() === 'inactive' || data.isActive === false;
            normalized.status = isInactive ? 'Inactive' : 'Active';
            normalized.isActive = !isInactive;
            
            employees.push(normalized);
        });

        console.log(`[EmployeeService] Raw employees from Firestore: ${employees.length}`);

        // Client-side Filtering

        // 1. Department Filter
        if (filters.department) {
            const filterDept = filters.department.toUpperCase().trim();
            employees = employees.filter(e => {
                const empDept = (e.department || '').toUpperCase().trim();
                return empDept === filterDept;
            });
        }

        // 2. Designation Filter
        if (filters.designation) {
            employees = employees.filter(e => e.designation === filters.designation);
        }

        // 3. Status Filter
        // Default behavior: Return Active employees only, unless explicitly asking for 'Inactive' or 'All'
        const requestedStatus = filters.status;
        if (requestedStatus === 'Inactive') {
            employees = employees.filter(e => e.status === 'Inactive' || e.isActive === false);
        } else if (requestedStatus === 'All' || filters.includeInactive === true) {
            // Include both Active and Inactive (no filter)
        } else {
            // Default: 'Active' only
            employees = employees.filter(e => e.status === 'Active' && e.isActive !== false);
        }

        // 4. Employment Type Filter
        if (filters.employmentType) {
            employees = employees.filter(e => e.employmentType === filters.employmentType);
        }

        // 5. Search Filter
        if (filters.search) {
            const term = filters.search.toLowerCase();
            employees = employees.filter(e =>
                (e.firstName || '').toLowerCase().includes(term) ||
                (e.lastName || '').toLowerCase().includes(term) ||
                (e.email || '').toLowerCase().includes(term) ||
                (e.employeeId || '').toLowerCase().includes(term) ||
                (e.personal?.fullName || '').toLowerCase().includes(term)
            );
        }

        // Sort: Newest First
        employees.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

        console.log(`[EmployeeService] Returning ${employees.length} employees after filters`);

        return {
            success: true,
            data: employees,
            count: employees.length
        };
    } catch (error) {
        console.error('Error fetching employees:', error);
        return { success: true, data: [] };
    }
};
// In-memory cache for manager names to avoid redundant Firestore reads
const managerNameCache = new Map();

/**
 * Resolve manager/employee full name by ID or employeeCode
 */
export const getManagerName = async (managerIdOrCode) => {
    if (!managerIdOrCode || managerIdOrCode === 'N/A' || managerIdOrCode === 'None') return '';

    // Check memory cache
    if (managerNameCache.has(managerIdOrCode)) {
        return managerNameCache.get(managerIdOrCode);
    }

    try {
        // 1. Try finding in 'employees' by document ID
        const empDocRef = doc(db, EMPLOYEES_COLLECTION, managerIdOrCode);
        const empDocSnap = await getDoc(empDocRef);
        if (empDocSnap.exists()) {
            const data = empDocSnap.data();
            const fullName = data.personal?.fullName ||
                `${data.personal?.firstName || ''} ${data.personal?.lastName || ''}`.trim() ||
                data.fullName ||
                `${data.firstName || ''} ${data.lastName || ''}`.trim();
            if (fullName) {
                managerNameCache.set(managerIdOrCode, fullName);
                return fullName;
            }
        }

        // 2. Try querying 'employees' by employeeCode
        const qCode = query(collection(db, EMPLOYEES_COLLECTION), where('job.employeeCode', '==', managerIdOrCode));
        const snapCode = await getDocs(qCode);
        if (!snapCode.empty) {
            const data = snapCode.docs[0].data();
            const fullName = data.personal?.fullName ||
                `${data.personal?.firstName || ''} ${data.personal?.lastName || ''}`.trim() ||
                data.fullName ||
                `${data.firstName || ''} ${data.lastName || ''}`.trim();
            if (fullName) {
                managerNameCache.set(managerIdOrCode, fullName);
                return fullName;
            }
        }

        // 3. Try querying 'employees' by root employeeId
        const qEmpId = query(collection(db, EMPLOYEES_COLLECTION), where('employeeId', '==', managerIdOrCode));
        const snapEmpId = await getDocs(qEmpId);
        if (!snapEmpId.empty) {
            const data = snapEmpId.docs[0].data();
            const fullName = data.personal?.fullName ||
                `${data.personal?.firstName || ''} ${data.personal?.lastName || ''}`.trim() ||
                data.fullName ||
                `${data.firstName || ''} ${data.lastName || ''}`.trim();
            if (fullName) {
                managerNameCache.set(managerIdOrCode, fullName);
                return fullName;
            }
        }

        // 4. Try 'users' collection
        const userDocRef = doc(db, 'users', managerIdOrCode);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
            const data = userDocSnap.data();
            const fullName = data.personal?.fullName ||
                `${data.personal?.firstName || ''} ${data.personal?.lastName || ''}`.trim() ||
                data.displayName ||
                data.name ||
                data.fullName;
            if (fullName) {
                managerNameCache.set(managerIdOrCode, fullName);
                return fullName;
            }
        }
    } catch (err) {
        console.warn('[EmployeeService] Error resolving manager name:', err);
    }

    return managerIdOrCode;
};

/**
 * Get single employee by ID
 * Fetches from 'employees' collection (primary source)
 */
export const getEmployee = async (id) => {
    try {
        // Fetch from 'employees' collection
        const employeeRef = doc(db, EMPLOYEES_COLLECTION, id);
        const employeeDoc = await getDoc(employeeRef);

        if (!employeeDoc.exists()) {
            throw new Error('Employee not found');
        }

        const data = employeeDoc.data();
        console.log(`[EmployeeService] Found employee:`, data);

        // The data already has the correct structure (personal, job, bank, salary)
        const personal = data.personal || {};
        const job = data.job || {};
        const bank = data.bank || {};
        const salary = data.salary || {};

        // Resolve reporting manager name
        let reportingManagerName = job.reportingManagerName || '';
        const mgrRef = job.reportingManager || data.jobDetails?.reportingTo;
        if (!reportingManagerName && mgrRef) {
            try {
                reportingManagerName = await getManagerName(mgrRef);
            } catch (e) {
                console.warn('[EmployeeService] Failed to resolve manager name:', e);
            }
        }

        // Handle timestamps
        const createdAt = data.createdAt?.toDate?.()?.toISOString() || data.createdAt;
        const updatedAt = data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt;

        return {
            success: true,
            data: {
                id: employeeDoc.id,
                ...data,
                personal: {
                    ...personal,
                    fullName: personal.fullName || `${personal.firstName || ''} ${personal.lastName || ''}`.trim(),
                    currentAddress: personal.currentAddress || {},
                    permanentAddress: personal.permanentAddress || {},
                    emergencyContact: personal.emergencyContact || {}
                },
                job: {
                    ...job,
                    employeeCode: job.employeeCode || id,
                    reportingManagerName: reportingManagerName || job.reportingManagerName || ''
                },
                bank: {
                    ...bank
                },
                salary: {
                    ...salary
                },
                // Root level fields for display
                firstName: personal.firstName || '',
                lastName: personal.lastName || '',
                email: personal.email || '',
                employeeId: job.employeeCode || id,
                department: job.department || '',
                designation: job.designation || '',
                status: data.status || 'Active',
                isActive: data.status === 'Active' || data.isActive !== false,
                createdAt,
                updatedAt
            }
        };
    } catch (error) {
        if (error.message === 'Employee not found') {
            console.warn(`[EmployeeService] Employee doc not found for ID: ${id}`);
        } else {
            console.error('Error fetching employee:', error);
        }
        throw new Error(error.message || 'Failed to fetch employee');
    }
};

/**
 * Create new employee
 * Also creates Firebase Auth user so employee can login
 */
export const createEmployee = async (employeeData) => {
    try {
        const employeesRef = collection(db, EMPLOYEES_COLLECTION);
        const newDocRef = doc(employeesRef); // Generate ID for record
        const docId = newDocRef.id;

        const personal = employeeData.personal || {};
        const job = employeeData.job || {};
        const bank = employeeData.bank || {};
        const initialPassword = employeeData.initialPassword || 'Welcome@123';

        // Handling File (Bank Document/Passbook) via Base64
        if (bank.document instanceof File) {
            console.log('[EmployeeService] Converting bank document to Base64...');
            const uploadedDoc = await fileToBase64(bank.document);
            if (uploadedDoc) {
                // Save the data URL directly in bank.document
                employeeData.bank.document = uploadedDoc.data;
                // Add to internal documents list for tracking
                if (!employeeData.documents) employeeData.documents = [];
                employeeData.documents.push({
                    name: uploadedDoc.name,
                    type: uploadedDoc.type,
                    uploadedAt: uploadedDoc.uploadedAt,
                    isBankDoc: true
                });
            }
        }

        // Handling Profile Photo (if file)
        if (personal.photo instanceof File) {
            const uploadedPhoto = await fileToBase64(personal.photo);
            if (uploadedPhoto) {
                employeeData.personal.photo = uploadedPhoto.data;
            }
        }

        // Handling Aadhaar Scan (if file)
        if (personal.aadhaarScan instanceof File) {
            const uploadedAadhaar = await fileToBase64(personal.aadhaarScan);
            if (uploadedAadhaar) {
                employeeData.personal.aadhaarScan = uploadedAadhaar.data;
                // Update documents list
                if (!employeeData.documents) employeeData.documents = [];
                employeeData.documents.push({
                    name: uploadedAadhaar.name,
                    type: uploadedAadhaar.type,
                    uploadedAt: uploadedAadhaar.uploadedAt,
                    isAadhaarDoc: true
                });
            }
        }

        // Handling PAN Scan (if file)
        if (personal.panScan instanceof File) {
            const uploadedPan = await fileToBase64(personal.panScan);
            if (uploadedPan) {
                employeeData.personal.panScan = uploadedPan.data;
                // Update documents list
                if (!employeeData.documents) employeeData.documents = [];
                employeeData.documents.push({
                    name: uploadedPan.name,
                    type: uploadedPan.type,
                    uploadedAt: uploadedPan.uploadedAt,
                    isPanDoc: true
                });
            }
        }

        const fullName = `${personal.firstName || ''} ${personal.middleName || ''} ${personal.lastName || ''}`.replace(/\s+/g, ' ').trim();

        // First, create Firebase Auth user so employee can login
        let authUserId = null;
        if (personal.email) {
            try {
                const { createUserWithEmailAndPassword } = await import('firebase/auth');
                const { auth } = await import('../../config/firebase');
                
                // Store current user to restore after creating new user
                const currentUser = auth.currentUser;
                
                // Create auth user for the new employee
                const userCredential = await createUserWithEmailAndPassword(auth, personal.email, initialPassword);
                authUserId = userCredential.user.uid;
                
                console.log(`[EmployeeService] Created Firebase Auth user for ${personal.email}`);
                
                // Sign out the newly created user and restore admin session
                // Note: This will sign out the admin, so we need to handle this differently
                // For now, we'll just create the user - admin will need to re-login
            } catch (authError) {
                // If user already exists, that's okay - they can use existing credentials
                if (authError.code === 'auth/email-already-in-use') {
                    console.log(`[EmployeeService] Auth user already exists for ${personal.email}`);
                } else {
                    console.warn('[EmployeeService] Could not create auth user:', authError.message);
                    // Don't fail the whole operation - employee record can still be created
                }
            }
        }

        const data = {
            ...employeeData,
            // Update the nested personal object with generated fullName
            personal: {
                ...personal,
                fullName
            },
            // Root level fields for listing
            firstName: personal.firstName,
            lastName: personal.lastName,
            email: personal.email,
            employeeId: job.employeeCode,
            department: job.department,
            designation: job.designation,
            status: 'Active',
            isActive: true, // Legacy support
            authUserId: authUserId, // Link to Firebase Auth user

            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),

            // Root level dates (converted to Timestamp) and kept in nested for consistency
            dob: personal.dateOfBirth ? Timestamp.fromDate(new Date(personal.dateOfBirth)) : null,
            joiningDate: job.joiningDate ? Timestamp.fromDate(new Date(job.joiningDate)) : null,
        };

        // Store initial password for Admin/HR visibility (NOT user-changed passwords)
        // This allows Admin & HR to see the password set during employee creation
        data.loginPassword = initialPassword;
        delete data.initialPassword; // Remove the field name alias

        // Use setDoc with the pre-generated docRef
        // Apply cleanup to remove any transient File objects or undefined values
        const finalData = cleanupForFirestore(data);
        await import('firebase/firestore').then(f => f.setDoc(newDocRef, finalData));

        return {
            success: true,
            message: 'Employee created successfully',
            data: {
                id: docId,
                ...finalData
            }
        };
    } catch (error) {
        console.error('Error creating employee:', error);
        throw new Error(error.message || 'Failed to create employee');
    }
};

/**
 * Update employee
 * Updates in 'employees' collection
 */
export const updateEmployee = async (id, employeeData) => {
    try {
        const employeeRef = doc(db, EMPLOYEES_COLLECTION, id);
        const employeeDoc = await getDoc(employeeRef);
        
        if (!employeeDoc.exists()) {
            throw new Error('Employee not found');
        }

        const currentData = employeeDoc.data();

        // Handling File Update (Bank Document/Passbook) via Base64
        const bankData = employeeData.bank || {};
        if (bankData.document instanceof File) {
            console.log('[EmployeeService] Converting new bank document to Base64...');
            const uploadedDoc = await fileToBase64(bankData.document);
            if (uploadedDoc) {
                // Save the data URL directly in bank.document
                employeeData.bank.document = uploadedDoc.data;
                // Update general documents list
                if (!employeeData.documents) employeeData.documents = currentData.documents || [];
                employeeData.documents.push({
                    name: uploadedDoc.name,
                    type: uploadedDoc.type,
                    uploadedAt: uploadedDoc.uploadedAt,
                    isBankDoc: true
                });
            }
        }

        // Handling Profile Photo (if file)
        const personalData = employeeData.personal || {};
        if (personalData.photo instanceof File) {
            const uploadedPhoto = await fileToBase64(personalData.photo);
            if (uploadedPhoto) {
                employeeData.personal.photo = uploadedPhoto.data;
            }
        }

        // Handling Aadhaar Scan (if file)
        if (personalData.aadhaarScan instanceof File) {
            const uploadedAadhaar = await fileToBase64(personalData.aadhaarScan);
            if (uploadedAadhaar) {
                employeeData.personal.aadhaarScan = uploadedAadhaar.data;
                // Update documents list
                if (!employeeData.documents) employeeData.documents = currentData.documents || [];
                employeeData.documents.push({
                    name: uploadedAadhaar.name,
                    type: uploadedAadhaar.type,
                    uploadedAt: uploadedAadhaar.uploadedAt,
                    isAadhaarDoc: true
                });
            }
        }

        // Handling PAN Scan (if file)
        if (personalData.panScan instanceof File) {
            const uploadedPan = await fileToBase64(personalData.panScan);
            if (uploadedPan) {
                employeeData.personal.panScan = uploadedPan.data;
                // Update documents list
                if (!employeeData.documents) employeeData.documents = currentData.documents || [];
                employeeData.documents.push({
                    name: uploadedPan.name,
                    type: uploadedPan.type,
                    uploadedAt: uploadedPan.uploadedAt,
                    isPanDoc: true
                });
            }
        }

        // Merge updates with existing data
        const personal = { ...currentData.personal, ...employeeData.personal };
        const job = { ...currentData.job, ...employeeData.job };
        const bank = { ...currentData.bank, ...employeeData.bank };
        const salary = { ...currentData.salary, ...employeeData.salary };
        const documents = employeeData.documents || currentData.documents || [];
        const careerJourney = employeeData.careerJourney || employeeData.workHistory || currentData.careerJourney || currentData.workHistory || [];
        
        const fullName = `${personal.firstName || ''} ${personal.middleName || ''} ${personal.lastName || ''}`.replace(/\s+/g, ' ').trim();

        const updateData = {
            personal: { ...personal, fullName },
            job: job,
            bank: bank,
            salary: salary,
            documents: documents,
            careerJourney: careerJourney,
            workHistory: careerJourney,
            // Synchronize root-level fields for easy querying
            firstName: personal.firstName,
            lastName: personal.lastName,
            email: personal.email,
            department: job.department,
            designation: job.designation,
            photoURL: personal.photo, // Sync to root for easy display in lists
            updatedAt: Timestamp.now()
        };

        // Handle status/isActive updates (for activate/deactivate functionality)
        if (employeeData.isActive !== undefined) {
            updateData.isActive = employeeData.isActive;
            updateData.status = employeeData.isActive ? 'Active' : 'Inactive';
        }
        if (employeeData.status !== undefined) {
            updateData.status = employeeData.status;
            updateData.isActive = employeeData.status === 'Active';
        }

        const finalUpdate = cleanupForFirestore(updateData);
        await updateDoc(employeeRef, finalUpdate);

        console.log(`[EmployeeService] Updated employee ${id}`, finalUpdate);

        return {
            success: true,
            message: 'Employee updated successfully',
            data: {
                id,
                ...finalUpdate
            }
        };
    } catch (error) {
        console.error('Error updating employee:', error);
        throw new Error(error.message || 'Failed to update employee');
    }
};

/**
 * Delete employee (soft delete - mark as deleted so it won't show anywhere)
 */
export const deleteEmployee = async (id) => {
    try {
        const employeeRef = doc(db, EMPLOYEES_COLLECTION, id);
        const employeeDoc = await getDoc(employeeRef);
        
        if (!employeeDoc.exists()) {
            throw new Error('Employee not found');
        }

        // Soft delete - mark as deleted (won't show in any list including inactive)
        await updateDoc(employeeRef, {
            isDeleted: true,
            isActive: false,
            status: 'Deleted',
            deletedAt: Timestamp.now(),
            updatedAt: Timestamp.now()
        });

        console.log(`[EmployeeService] Deleted employee ${id}`);

        return {
            success: true,
            message: 'Employee deleted successfully'
        };
    } catch (error) {
        console.error('Error deleting employee:', error);
        throw new Error(error.message || 'Failed to delete employee');
    }
};

/**
 * Get all unique departments
 */
export const getDepartments = async () => {
    try {
        const employeesRef = collection(db, EMPLOYEES_COLLECTION);
        const snapshot = await getDocs(employeesRef);

        const departments = new Set();
        snapshot.forEach((doc) => {
            const dept = doc.data().department;
            if (dept) {
                departments.add(dept);
            }
        });

        return {
            success: true,
            data: Array.from(departments).sort()
        };
    } catch (error) {
        console.error('Error fetching departments:', error);
        throw new Error(error.message || 'Failed to fetch departments');
    }
};

/**
 * Get employees by department
 */
export const getEmployeesByDepartment = async (department) => {
    try {
        const employeesRef = collection(db, EMPLOYEES_COLLECTION);
        const q = query(
            employeesRef,
            where('department', '==', department),
            where('isActive', '==', true)
        );

        const snapshot = await getDocs(q);
        const employees = [];

        snapshot.forEach((doc) => {
            employees.push({
                id: doc.id,
                ...doc.data()
            });
        });

        return {
            success: true,
            data: employees
        };
    } catch (error) {
        console.error('Error fetching employees by department:', error);
        throw new Error(error.message || 'Failed to fetch employees by department');
    }
};

/**
 * Get employee by email
 */
export const getEmployeeByEmail = async (email) => {
    try {
        if (!email) {
            throw new Error('Email is required');
        }

        const normalizedEmail = email.toLowerCase().trim();
        console.log(`[EmployeeService] Looking up employee by email: "${normalizedEmail}"`);

        const employeesRef = collection(db, EMPLOYEES_COLLECTION);
        const snapshot = await getDocs(employeesRef);

        let employee = null;

        for (const docSnap of snapshot.docs) {
            const data = docSnap.data();
            const rootEmail = (data.email || '').toLowerCase().trim();
            const personalEmail = (data.personal?.email || '').toLowerCase().trim();
            const personalInfoEmail = (data.personalInfo?.email || '').toLowerCase().trim();

            if (rootEmail === normalizedEmail ||
                personalEmail === normalizedEmail ||
                personalInfoEmail === normalizedEmail) {
                console.log(`[EmployeeService] Found match in doc: ${docSnap.id}`);
                employee = normalizeEmployeeData(data, docSnap.id);
                break;
            }
        }

        if (employee) {
            return {
                success: true,
                data: employee
            };
        }

        console.log(`[EmployeeService] No employee found matching email: "${normalizedEmail}"`);
        return {
            success: false,
            data: null,
            message: 'Employee not found with this email'
        };
    } catch (error) {
        console.error('Error fetching employee by email:', error);
        return {
            success: false,
            data: null,
            message: error.message || 'Failed to fetch employee'
        };
    }
};

/**
 * Get employee by UID
 */
export const getEmployeeByUid = async (uid) => {
    try {
        if (!uid) {
            throw new Error('UID is required');
        }

        console.log(`[EmployeeService] Looking up employee by UID: "${uid}"`);
        const employeesRef = collection(db, EMPLOYEES_COLLECTION);
        const snapshot = await getDocs(employeesRef);

        let employee = null;

        for (const docSnap of snapshot.docs) {
            const data = docSnap.data();
            if (data.uid === uid ||
                data.firebaseUid === uid ||
                data.authUserId === uid ||
                docSnap.id === uid) {
                console.log(`[EmployeeService] Found match for UID in doc: ${docSnap.id}`);
                employee = normalizeEmployeeData(data, docSnap.id);
                break;
            }
        }

        if (employee) {
            return {
                success: true,
                data: employee
            };
        }

        console.log(`[EmployeeService] No employee found matching UID: "${uid}"`);
        return {
            success: false,
            data: null,
            message: 'Employee not found with this UID'
        };
    } catch (error) {
        console.error('Error fetching employee by UID:', error);
        return {
            success: false,
            data: null,
            message: error.message || 'Failed to fetch employee'
        };
    }
};

/**
 * Update employee status (Active/Inactive) and sync with Firebase Auth
 */
export const updateEmployeeStatus = async (id, newStatus, email) => {
    try {
        const employeeRef = doc(db, EMPLOYEES_COLLECTION, id);
        const employeeDoc = await getDoc(employeeRef);
        
        if (!employeeDoc.exists()) {
            throw new Error('Employee not found');
        }

        const isActive = newStatus === 'Active';

        const updatePayload = {
            status: newStatus,
            isActive: isActive,
            updatedAt: Timestamp.now()
        };

        if (!isActive) {
            updatePayload.inactiveDate = Timestamp.now();
            updatePayload.exitDate = Timestamp.now();
            updatePayload.deactivatedAt = Timestamp.now();
        }

        // Update Firestore employees collection
        await updateDoc(employeeRef, updatePayload);

        // Also sync to users collection if a document exists with this ID
        try {
            const userRef = doc(db, 'users', id);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
                await updateDoc(userRef, updatePayload);
            }
        } catch (e) {
            console.warn('[EmployeeService] Sync to users collection skipped:', e);
        }

        console.log(`[EmployeeService] Updated employee ${id} status to ${newStatus} (isActive: ${isActive})`);

        return {
            success: true,
            message: `Employee ${isActive ? 'activated' : 'deactivated'} successfully`,
            data: { id, status: newStatus, isActive }
        };
    } catch (error) {
        console.error('Error updating employee status:', error);
        throw new Error(error.message || 'Failed to update employee status');
    }
};

// ── 🌟 CAREER GROWTH & MILESTONE TRACKING SYSTEM ──────────────────────────────

/**
 * Add a career growth / milestone entry to an employee
 * (Promotion, Salary Revision, Role Change, Achievement, Recognition, Appraisal)
 */
export const addCareerMilestone = async (id, milestoneData, currentUser, syncProfile = true) => {
    try {
        const employeeRef = doc(db, EMPLOYEES_COLLECTION, id);
        const employeeDoc = await getDoc(employeeRef);
        
        if (!employeeDoc.exists()) {
            throw new Error('Employee not found');
        }

        const currentData = employeeDoc.data();
        const existingJourney = currentData.careerJourney || currentData.workHistory || [];

        const milestoneId = milestoneData.id || `milestone_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
        
        const newMilestone = {
            id: milestoneId,
            type: milestoneData.type || 'promotion', // 'promotion', 'salary_increment', 'role_change', 'achievement', 'award', 'certification', 'appraisal'
            title: milestoneData.title || 'Career Milestone',
            effectiveDate: milestoneData.effectiveDate || new Date().toISOString().split('T')[0],
            
            // Promotion / Role Details
            previousDesignation: milestoneData.previousDesignation || '',
            newDesignation: milestoneData.newDesignation || '',
            previousDepartment: milestoneData.previousDepartment || '',
            newDepartment: milestoneData.newDepartment || '',
            previousRole: milestoneData.previousRole || '',
            newRole: milestoneData.newRole || '',
            
            // Salary Revision Details
            previousSalary: parseFloat(milestoneData.previousSalary) || 0,
            newSalary: parseFloat(milestoneData.newSalary) || 0,
            incrementAmount: parseFloat(milestoneData.incrementAmount) || 0,
            incrementPercentage: parseFloat(milestoneData.incrementPercentage) || 0,
            previousCTC: parseFloat(milestoneData.previousCTC) || 0,
            newCTC: parseFloat(milestoneData.newCTC) || 0,
            
            // Achievement & Recognition Details
            achievementCategory: milestoneData.achievementCategory || '',
            badgeName: milestoneData.badgeName || '',
            
            // Notes & Description
            description: milestoneData.description || '',
            keyAchievements: milestoneData.keyAchievements || [],
            documentUrl: milestoneData.documentUrl || '',
            
            // Audit info
            createdByName: currentUser?.employeeName || currentUser?.fullName || currentUser?.email || 'Admin / HR',
            createdByUid: currentUser?.uid || currentUser?.id || '',
            createdByRole: currentUser?.role || 'admin',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };

        const updatedJourney = [newMilestone, ...existingJourney];

        const updatePayload = {
            careerJourney: updatedJourney,
            workHistory: updatedJourney, // sync both field names for compatibility
            updatedAt: Timestamp.now()
        };

        // If syncProfile is requested, update active profile fields in employee record
        if (syncProfile) {
            // If new designation / department provided
            if (newMilestone.newDesignation || newMilestone.newDepartment || newMilestone.newRole) {
                const currentJob = currentData.job || {};
                updatePayload.job = {
                    ...currentJob,
                    designation: newMilestone.newDesignation || currentJob.designation,
                    department: newMilestone.newDepartment || currentJob.department,
                    role: newMilestone.newRole || currentJob.role || currentData.role
                };
                if (newMilestone.newDesignation) updatePayload.designation = newMilestone.newDesignation;
                if (newMilestone.newDepartment) updatePayload.department = newMilestone.newDepartment;
            }

            // If salary increment provided
            if (newMilestone.newSalary > 0) {
                const currentSalary = currentData.salary || {};
                updatePayload.salary = {
                    ...currentSalary,
                    basicSalary: newMilestone.newSalary
                };
            }
        }

        const cleanedPayload = cleanupForFirestore(updatePayload);
        await updateDoc(employeeRef, cleanedPayload);

        console.log(`[EmployeeService] Successfully added milestone ${milestoneId} for employee ${id}`);

        return {
            success: true,
            message: 'Career milestone added successfully',
            data: newMilestone
        };
    } catch (error) {
        console.error('Error adding career milestone:', error);
        throw new Error(error.message || 'Failed to add career milestone');
    }
};

/**
 * Update an existing career milestone
 */
export const updateCareerMilestone = async (id, milestoneId, updatedData, currentUser) => {
    try {
        const employeeRef = doc(db, EMPLOYEES_COLLECTION, id);
        const employeeDoc = await getDoc(employeeRef);
        
        if (!employeeDoc.exists()) {
            throw new Error('Employee not found');
        }

        const currentData = employeeDoc.data();
        const existingJourney = currentData.careerJourney || currentData.workHistory || [];

        const updatedJourney = existingJourney.map(m => {
            if (m.id === milestoneId) {
                return {
                    ...m,
                    ...updatedData,
                    updatedByName: currentUser?.employeeName || currentUser?.fullName || currentUser?.email || 'Admin / HR',
                    updatedByUid: currentUser?.uid || '',
                    updatedAt: new Date().toISOString()
                };
            }
            return m;
        });

        const updatePayload = {
            careerJourney: updatedJourney,
            workHistory: updatedJourney,
            updatedAt: Timestamp.now()
        };

        const cleanedPayload = cleanupForFirestore(updatePayload);
        await updateDoc(employeeRef, cleanedPayload);

        return {
            success: true,
            message: 'Career milestone updated successfully'
        };
    } catch (error) {
        console.error('Error updating career milestone:', error);
        throw new Error(error.message || 'Failed to update career milestone');
    }
};

/**
 * Delete a career milestone
 */
export const deleteCareerMilestone = async (id, milestoneId, currentUser) => {
    try {
        const employeeRef = doc(db, EMPLOYEES_COLLECTION, id);
        const employeeDoc = await getDoc(employeeRef);
        
        if (!employeeDoc.exists()) {
            throw new Error('Employee not found');
        }

        const currentData = employeeDoc.data();
        const existingJourney = currentData.careerJourney || currentData.workHistory || [];

        const updatedJourney = existingJourney.filter(m => m.id !== milestoneId);

        const updatePayload = {
            careerJourney: updatedJourney,
            workHistory: updatedJourney,
            updatedAt: Timestamp.now()
        };

        const cleanedPayload = cleanupForFirestore(updatePayload);
        await updateDoc(employeeRef, cleanedPayload);

        return {
            success: true,
            message: 'Career milestone removed successfully'
        };
    } catch (error) {
        console.error('Error deleting career milestone:', error);
        throw new Error(error.message || 'Failed to delete career milestone');
    }
};

/**
 * Get employee career journey (with synthesized baseline joining milestone if empty)
 */
export const getEmployeeCareerJourney = async (id) => {
    try {
        const employeeRes = await getEmployee(id);
        if (!employeeRes.success || !employeeRes.data) {
            return { success: true, data: [] };
        }
        const emp = employeeRes.data;
        let journey = emp.careerJourney || emp.workHistory || [];

        // If no milestones recorded yet, synthesize a baseline "Joined Organization" milestone
        if (journey.length === 0 && (emp.job?.joiningDate || emp.joiningDate)) {
            const joinDate = emp.job?.joiningDate || emp.joiningDate;
            const formattedDate = joinDate?.toDate ? joinDate.toDate().toISOString().split('T')[0] : (typeof joinDate === 'string' ? joinDate.split('T')[0] : '');
            
            const initialSalary = parseFloat(emp.salary?.basicSalary) || 0;
            const joiningMilestone = {
                id: 'baseline_joining',
                type: 'joining',
                title: 'Joined Digi Sahyadri',
                effectiveDate: formattedDate || new Date().toISOString().split('T')[0],
                newDesignation: emp.job?.designation || emp.designation || 'Team Member',
                newDepartment: emp.job?.department || emp.department || '',
                newRole: emp.job?.role || emp.role || 'employee',
                newSalary: initialSalary,
                newCTC: initialSalary * 12,
                description: `Officially joined Digi Sahyadri in the ${emp.job?.department || emp.department || 'organization'} department as ${emp.job?.designation || emp.designation || 'Team Member'}.`,
                createdByName: 'System Baseline',
                createdAt: formattedDate || new Date().toISOString()
            };
            journey = [joiningMilestone];
        }

        return {
            success: true,
            data: journey
        };
    } catch (error) {
        console.error('Error fetching career journey:', error);
        return { success: true, data: [] };
    }
};

const employeeService = {
    getEmployees,
    getEmployee,
    createEmployee,
    updateEmployee,
    deleteEmployee,
    getDepartments,
    getEmployeesByDepartment,
    getEmployeeByEmail,
    getEmployeeByUid,
    updateEmployeeStatus,
    getManagerName,
    addCareerMilestone,
    updateCareerMilestone,
    deleteCareerMilestone,
    getEmployeeCareerJourney
};

export default employeeService;
